import * as zod from 'zod'

const MAX_CHARGE_HOURS = 48

const cutoffTimeSchema = zod.coerce
  .number({
    invalid_type_error: 'Cutoff time hours must be a number',
    required_error: 'Cutoff time hours is required',
  })
  .min(1, { message: 'Cutoff time hours is required' })
  .max(MAX_CHARGE_HOURS, {
    message: `Cutoff time hours must be less than ${MAX_CHARGE_HOURS}`,
  })

const policyWindowSchema = zod
  .object({
    cutoffTime: cutoffTimeSchema,
    chargeType: zod.enum(['percentage', 'minutes'], {
      required_error: 'Charge type is required',
    }),
    chargePercentage: zod.coerce
      .number({
        invalid_type_error: 'Charge percentage must be a number',
      })
      .optional(),
    chargeHours: zod.coerce
      .number({
        invalid_type_error: 'Charge hours must be a number',
      })
      .optional(),
  })
  .superRefine((window, ctx) => {
    if (window.chargeType === 'percentage') {
      if (
        window.chargePercentage == null ||
        Number.isNaN(window.chargePercentage)
      ) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['chargePercentage'],
          message: 'Charge percentage is required',
        })
        return
      }
      if (window.chargePercentage < 1) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['chargePercentage'],
          message: 'Charge percentage is required',
        })
      }
      if (window.chargePercentage > 100) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['chargePercentage'],
          message: 'Charge percentage must be less than 100',
        })
      }
      return
    }

    if (window.chargeHours == null || Number.isNaN(window.chargeHours)) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ['chargeHours'],
        message: 'Charge hours is required',
      })
      return
    }
    if (window.chargeHours <= 0) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ['chargeHours'],
        message: 'Charge hours must be greater than 0',
      })
    }
    if (window.chargeHours > MAX_CHARGE_HOURS) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ['chargeHours'],
        message: `Charge hours must be ${MAX_CHARGE_HOURS} or less`,
      })
    }
  })

export const addCancellationPolicySchema = zod.object({
  startsAt: zod.date({
    required_error: 'Start date is required',
  }),
  policyWindows: zod
    .array(policyWindowSchema, {
      required_error: 'Please add at least one window',
    })
    .min(1, { message: 'Please add at least one window' }),
})

export type AddCancellationPolicySchema = zod.infer<
  typeof addCancellationPolicySchema
>

/** Empty-field shape for react-hook-form before zod validation runs. */
export type AddCancellationPolicyFormValues = {
  startsAt: Date
  policyWindows: {
    cutoffTime?: number
    chargeType: 'percentage' | 'minutes'
    chargePercentage?: number
    chargeHours?: number
  }[]
}

export const emptyPolicyWindow =
  (): AddCancellationPolicyFormValues['policyWindows'][number] => ({
    chargeType: 'percentage',
  })
